import React from 'react';
import styled from 'styled-components';

import { GUID, ValidationMessage, ValidationResult } from '@ndvr/customer-api';

import { themeDefault } from 'styles';
import {
    AccountGroupNameLink,
    AccountNameLink,
    GuidDisplay,
    HouseholdNameLink,
    IconCheckmark,
    OpportunityNameLink,
    PersonOrgNameLink,
} from 'widgets';
import { Button } from 'widgets/button';
import { IconAlert } from 'widgets/icon';
import { HorizontalLayout, VerticalLayout } from 'widgets/Layout';
import { Modal } from 'widgets/Modal';
import { Text, Title } from 'widgets/Text';

type ValidationResultModalProps = {
    result: ValidationResult;
    validResultMessage: string;
    invalidResultMessage: string;
    onClose: () => void;
};
export const ValidationResultModal = ({
    result,
    validResultMessage,
    invalidResultMessage,
    onClose,
}: ValidationResultModalProps) => {
    return (
        <Modal onClose={onClose} $maxWidth="800px">
            <VerticalLayout $gap={16} $padding="40px">
                <VerticalLayout $gap={16} $columnCenter>
                    {result.valid ? (
                        <>
                            <IconCheckmark size="large" color={themeDefault.text.positive} />
                            <Title modal>{validResultMessage}</Title>
                        </>
                    ) : (
                        <>
                            <IconAlert size="large" color={themeDefault.text.negative} />
                            <Title modal>{invalidResultMessage}</Title>
                        </>
                    )}
                </VerticalLayout>
                <VerticalLayout $gap={8}>
                    {result.errors.length > 0 && <Title section>Errors</Title>}
                    <ValidationMessages messages={result.errors} error />
                </VerticalLayout>
                <VerticalLayout $gap={8}>
                    {result.warnings.length > 0 && <Title section>Warnings</Title>}
                    <ValidationMessages messages={result.warnings} />
                </VerticalLayout>
            </VerticalLayout>
            <Separator />
            <HorizontalLayout $center $padding="32px">
                <Button buttonType="secondary" size="large" onClick={onClose}>
                    Close
                </Button>
            </HorizontalLayout>
        </Modal>
    );
};

const Separator = styled.div`
    width: 100%;
    border-top: 1px solid ${({ theme }) => theme.border.light};
`;

const ValidationMessages = ({ messages, error = false }: { messages: ValidationMessage[]; error?: boolean }) => {
    return (
        <>
            {messages.map((message, index) => (
                <VerticalLayout key={index} $gap={8}>
                    <ValidationMessageRow $error={error} $apart $rowCenter>
                        <Text>{message.message}</Text>
                        {message.refEntityType && message.refEntityGUID && (
                            <HorizontalLayout $gap={16} $rowCenter>
                                <EntityLink entityType={message.refEntityType} entityGUID={message.refEntityGUID} />
                                <GuidDisplay guid={message.refEntityGUID} />
                            </HorizontalLayout>
                        )}
                    </ValidationMessageRow>
                    {message.children.length > 0 && (
                        <VerticalLayout $gap={8} $padding="0 0 0 32px">
                            <ValidationMessages messages={message.children} error={error} />
                        </VerticalLayout>
                    )}
                </VerticalLayout>
            ))}
        </>
    );
};

const ValidationMessageRow = styled(HorizontalLayout)<{ $error?: boolean }>`
    height: 44px;
    padding: 0 2px 0 8px;
    border: 1px solid ${({ theme }) => theme.border.dark};
    border-left: 4px solid ${({ $error, theme }) => ($error ? theme.intent.negative : theme.intent.warn)};
    border-radius: 2px;
`;

const EntityLink = ({ entityType, entityGUID }: { entityType: string; entityGUID: GUID<any> }) => {
    switch (entityType) {
        case 'GlobalAccount':
            return <HouseholdNameLink householdGUID={entityGUID} />;
        case 'PersonOrg':
            return <PersonOrgNameLink personOrgGUID={entityGUID} />;
        case 'AccountGroup':
            return <AccountGroupNameLink accountGroupGUID={entityGUID} />;
        case 'Account':
            return <AccountNameLink accountGUID={entityGUID} />;
        case 'Opportunity':
            return <OpportunityNameLink opportunityGUID={entityGUID} />;
        default:
            return <Text>{entityType}</Text>;
    }
};
